import React, { SetStateAction } from "react";
import { Input } from "../../../../shadcn/components/ui/input";
import { Checkbox } from "../../../../shadcn/components/ui/checkbox";
import { Switch } from "../../../../shadcn/components/ui/switch";
import { Button } from "../../../../shadcn/components/ui/button";
import CustomMultiplePDFViewer from "../CustomMultiplePDFViewer";
import CustomMultipleFileUploader from "../CustomMultipleFileUploader";
import CheckIcon from "../../../../icons/CheckIcon";
import { convertAmmountToWords } from "services";
import { ConfirmCheckboxConfig } from "../Individual/IndividualBidderFormConfig";

interface BidderFormFieldsHelperProps {
  hideConditions?: boolean;
  formFieldType?: string;
  disableConditions?: boolean;
  id: string;
  value?: string | number | undefined | readonly string[];
  onChange?: (e?: any) => void;
  switchFieldValue?: boolean;
  setSwitchFieldValue?: (value: boolean) => void;
  formFieldErrors?: string;
  fieldLabel?: string;
  showVerifiedIcon?: boolean;
  isVerified?: boolean;
  isFetchingPan?: boolean;
  inputFieldClass?: string;
  confirmCheckbox?: ConfirmCheckboxConfig;
  mode?: string;
}

interface AcceptRejectComponentProps {
  mode: string | undefined;
  hideConditions: boolean;
  acceptButtonDisabled: boolean;
  handleAcceptAction: () => void;
  rejectButtonDisabled: boolean;
  handleRejectAction: () => void;
}

interface SubmitButtonComponentProps {
  mode: string | undefined;
  handleAcceptAction: () => void;
  acceptButtonDisabled: boolean;
  hideConditions: boolean;
  buttonLabel?: string;
  btnClass?: string;
}

interface BidderPDFViewerProps {
  renderKey: number;
  dataFiles: any;
  setDataFiles: any;
  headingText?: string;
  hideConditions: boolean;
  allowedMimeTypes?: any;
  property_uuid: string;
  owner_service_type?: string | undefined;
}

interface BidderDocumentUploaderProps {
  hideConditions: boolean;
  headingText?: string;
  disableConditions: boolean;
  allowedMimeTypes?: any;
  property_uuid: string;
  owner_service_type?: string | undefined;
  dataFiles: any;
  renderKey: number;
  setDataFiles: any;
}

interface JointBidderSubmitButtonProps {
  mode: string | undefined;
  activeStep: number | undefined;
  totalSteps: number | undefined;
  handlePreviousStep: () => void;
  handleSubmitAllBidders: () => void;
  handleNextStep: () => void;
  hideConditions: boolean;
}

const BidderFormFieldsHelper = ({
  hideConditions = false,
  formFieldType = "input",
  disableConditions = false,
  id,
  value,
  onChange,
  formFieldErrors,
  fieldLabel,
  switchFieldValue = false,
  showVerifiedIcon = false,
  isVerified = false,
  setSwitchFieldValue,
  inputFieldClass,
  confirmCheckbox,
  mode,
  isFetchingPan,
}: BidderFormFieldsHelperProps) => {
  console.log("🚀 ~ BidderFormFieldsHelper ~ mode:", mode);
  return !hideConditions ? (
    <div>
      {formFieldType === "input" ? (
        <>
          <div className={`relative w-full`}>
            {/* Only show spinner for PAN field when verifying */}
            {id === "pan" && isFetchingPan && (
              <div className="absolute top-1/3 right-2 z-10">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-orange"></div>
              </div>
            )}
            {/* Only show checkmark for PAN field when verified AND not currently fetching */}
            {id === "pan" && showVerifiedIcon && isVerified && !isFetchingPan && (
              <div className="absolute top-1/3 right-2">
                <CheckIcon className="h-5 w-5" />
              </div>
            )}
            <Input
              id={id}
              disabled={disableConditions}
              value={value}
              onChange={onChange}
              className={`bg-white peer block w-full appearance-none border pt-3 pb-0 pl-4 h-[50px] focus:pb-0 outline-none font-openSans font-normal text-web-body-sm lg:text-web-body-md leading-3 lg:leading-4 text-brand-textLightGrey focus:border-brand-orange ${inputFieldClass}`}
            />
            <label
              htmlFor="name"
              className={`absolute font-openSans font-normal  left-4 transition-all duration-300 -translate-y-1/2  text-gray-400 top-1/4 text-[12px] leading-[13px] peer-focus:text-brand-orange`}
            >
              {fieldLabel}
            </label>
            {formFieldErrors && (
              <p className="text-brand-textRed  mt-1 text-web-body-sm ml-2">
                {formFieldErrors}
              </p>
            )}
          </div>

          {id === "initial_bid_price" &&
            value &&
            !["joint-viewer", "view"].includes(mode as string) && (
              <div>
                <div className="mt-2 flex items-center gap-2">
                  <Checkbox
                    id={confirmCheckbox?.id ?? "initial_bid_price_confirmed"}
                    checked={confirmCheckbox?.value ?? false}
                    onCheckedChange={(checked: boolean) =>
                      confirmCheckbox?.onChange &&
                      confirmCheckbox.onChange({ target: { checked } } as any)
                    }
                    className="h-4 w-4 accent-brand-orange cursor-pointer"
                    disabled={disableConditions}
                  />

                  <p className="text-sm italic flex items-center gap-2">
                    <span className="font-semibold">Price in words:</span>
                    <span className="text-[#4DBB3E] font-semibold uppercase">
                      {convertAmmountToWords(Number(value)) || "Invalid amount"}
                    </span>
                  </p>
                </div>
                {confirmCheckbox?.formFieldErrors && (
                  <p className="text-brand-textRed mt-1 text-web-body-sm ml-2">
                    {confirmCheckbox.formFieldErrors}
                  </p>
                )}
              </div>
            )}
        </>
      ) : formFieldType === "switch_field" ? (
        <div key={id} className="flex gap-x-4 items-center ml-4 lg:ml-0">
          <Checkbox
            checkIconClassName="text-brand-bgBlack"
            className="font-openSans font-normal text-web-body-sm leading-4 data-[state=checked]:bg-[#EEEEEE] border-brand-bgBlack lg:hidden"
            id={`asset-${id}`}
            checked={switchFieldValue}
            onCheckedChange={setSwitchFieldValue}
          />
          <Switch
            disabled={disableConditions}
            thumbClassName="h-6 w-6 data-[state=unchecked]:bg-brand-textGrey"
            checked={switchFieldValue}
            onCheckedChange={setSwitchFieldValue}
            className="data-[state=checked]:bg-orange-500 data-[state=unchecked]:bg-transparent hidden lg:flex  data-[state=unchecked]:border-2 data-[state=unchecked]:border-brand-textGrey p-1 h-8 w-[52px] data-[state=unchecked]:text-brand-textGrey"
          />
          <p className="font-openSans font-normal text-web-body-sm leading-[14px] text-brand-textGrey">
            {fieldLabel}
          </p>
        </div>
      ) : (
        <Input className="hidden" />
      )}
    </div>
  ) : (
    <></>
  );
};

const AcceptRejectComponent = ({
  mode,
  hideConditions,
  acceptButtonDisabled,
  handleAcceptAction,
  rejectButtonDisabled,
  handleRejectAction,
}: AcceptRejectComponentProps) => {
  return hideConditions ? (
    <></>
  ) : (
    <div className="w-full flex flex-col md:flex-row justify-center md:justify-end  mt-10">
      <div className="space-x-3 md:space-x-3">
        <Button
          disabled={acceptButtonDisabled}
          onClick={handleAcceptAction}
          className="px-5 py-3 w-[140px] md:w-[125px]"
        >
          Accept
        </Button>
        <Button
          disabled={rejectButtonDisabled}
          onClick={handleRejectAction}
          className="bg-[#fafafabb] border text-[#B60100] px-5 py-3 w-[140px] md:w-[125px] hover:bg-brand-borderLightGray"
        >
          Reject
        </Button>
      </div>
    </div>
  );
};

const SubmitButtonComponent = ({
  mode,
  handleAcceptAction,
  acceptButtonDisabled,
  buttonLabel = "Submit",
  hideConditions,
  btnClass,
}: SubmitButtonComponentProps) => {
  return hideConditions ? (
    <></>
  ) : (
    <div className="w-full flex flex-col md:flex-row justify-center md:justify-end mt-10">
      <div className="space-x-3 md:space-x-3">
        <Button
          disabled={acceptButtonDisabled}
          onClick={handleAcceptAction}
          className={`px-5 py-3 w-[140px] md:w-[125px] ${btnClass}`}
        >
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
};

const BidderPDFViewer = ({
  renderKey,
  dataFiles,
  setDataFiles,
  headingText = "View KYC documents",
  hideConditions,
  allowedMimeTypes = ["application/pdf"],
  property_uuid,
  owner_service_type = "kyc-documents",
}: BidderPDFViewerProps) => {
  return hideConditions ? (
    <></>
  ) : (
    <div className="translate-y-2">
      <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
        {headingText}
      </p>
      <CustomMultiplePDFViewer
        key={renderKey}
        handleChange={setDataFiles}
        allowedMimeTypes={allowedMimeTypes}
        property_uuid={property_uuid}
        owner_service_type={owner_service_type}
        data={dataFiles}
      />
    </div>
  );
};

const BidderDocumentUploader = ({
  hideConditions,
  headingText = "Upload KYC documents",
  disableConditions,
  allowedMimeTypes,
  property_uuid,
  owner_service_type,
  dataFiles,
  renderKey,
  setDataFiles,
}: BidderDocumentUploaderProps) => {
  return hideConditions ? (
    <></>
  ) : (
    <div className="translate-y-2">
      <p className="font-openSans font-normal mb-2 text-mobile-h3 lg:text-web-subheading leading-8 lg:leading-6 lg:ml-5 text-bankerColor">
        {headingText}
      </p>
      <div
        className={` ${
          !disableConditions ? "" : "pointer-events-none opacity-50 select-none"
        }`}
      >
        <CustomMultipleFileUploader
          key={renderKey}
          handleChange={setDataFiles}
          allowedMimeTypes={allowedMimeTypes}
          property_uuid={property_uuid}
          owner_service_type={owner_service_type ?? "kyc-documents"}
          data={dataFiles}
        />
      </div>
    </div>
  );
};

const JointBidderSubmitButton = ({
  mode,
  activeStep,
  totalSteps,
  handlePreviousStep,
  handleSubmitAllBidders,
  handleNextStep,
  hideConditions = false,
}: JointBidderSubmitButtonProps) => {
  return !hideConditions ? (
    <div className="flex justify-end space-x-4">
      <Button onClick={handlePreviousStep}>Previous</Button>
      {activeStep !== undefined &&
      totalSteps &&
      activeStep === totalSteps - 1 ? (
        <Button onClick={handleSubmitAllBidders}>Submit All</Button>
      ) : (
        <Button
          onClick={() => {
            handleNextStep();
          }}
        >
          Next
        </Button>
      )}
    </div>
  ) : (
    <></>
  );
};

interface NotificationModalProps {
  isOpen: string;
  onClose: React.Dispatch<SetStateAction<string>>;
}

const NotificationModal = ({ isOpen, onClose }: NotificationModalProps) => {
  if (isOpen === "") return null;

  const messageText = () => {
    switch (isOpen) {
      // case "onboarded":
      //   return "The following bidder is an onboarded user.";
      case "registered":
        return "The following bidder is a registered user but has incomplete onboarding details.";
      case "unregistered":
        return "The following bidder is an unregistered user.";
      default:
        return "The following bidder is an unregistered user.";
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-md text-center relative">
        <div className="flex space-x-4 items-center">
          {/* Exclamation Icon */}
          <div className="flex justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-brand-orange"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
              />
            </svg>
          </div>

          <p className="mb-4 text-start  text-web-body-lg">{messageText()}</p>
        </div>
        <button
          onClick={() => onClose("")}
          className="bg-brand-bgBlack text-white px-3 w-28 py-1.5 rounded hover:bg-black text-web-body-md"
        >
          Ok
        </button>
      </div>
    </div>
  );
};

export {
  BidderFormFieldsHelper,
  AcceptRejectComponent,
  SubmitButtonComponent,
  BidderPDFViewer,
  BidderDocumentUploader,
  JointBidderSubmitButton,
  NotificationModal,
};
